import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { apiService } from '../services/apiService';

const TIME_RANGES = [
  { label: '1h', hours: 1, limit: 100, xFormat: 'HH:mm' },
  { label: '6h', hours: 6, limit: 400, xFormat: 'HH:mm' },
  { label: '12h', hours: 12, limit: 750, xFormat: 'HH:mm' },
  { label: '24h', hours: 24, limit: 1000, xFormat: 'HH:mm' },
  { label: '7d', hours: 168, limit: 1000, xFormat: 'EEE HH:mm' },
  { label: '30d', hours: 720, limit: 1000, xFormat: 'MMM dd' },
];

const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

function formatTimeLabel(timestamp, hours) {
  const range = TIME_RANGES.find(r => r.hours === hours);
  const fmt = range ? range.xFormat : 'HH:mm';
  try {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : new Date(timestamp);
    return format(date, fmt);
  } catch {
    return '';
  }
}

const WeatherHistorySection = ({ observatoryId }) => {
  const [selectedRange, setSelectedRange] = useState(TIME_RANGES[3]); // 24h default
  const [weatherData, setWeatherData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchData = useCallback(async () => {
    if (!observatoryId) return;
    try {
      setError(null);
      setLoading(true);

      const now = new Date();
      const startDate = new Date(now.getTime() - selectedRange.hours * 60 * 60 * 1000).toISOString();

      const [historyRes, statsRes] = await Promise.all([
        apiService.getWeatherHistory(observatoryId, {
          start_date: startDate,
          end_date: now.toISOString(),
          limit: selectedRange.limit,
          sort: 'desc',
        }),
        apiService.getWeatherHistoryStats(observatoryId, {
          hours: selectedRange.hours,
        }),
      ]);

      setWeatherData(historyRes?.data?.history || []);
      setStats(statsRes?.data?.stats || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load weather history');
      setWeatherData([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [observatoryId, selectedRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleRangeChange = (event, newValue) => {
    if (newValue !== null) {
      const range = TIME_RANGES.find(r => r.label === newValue);
      if (range) setSelectedRange(range);
    }
  };

  const chartData = useMemo(() => {
    if (!weatherData?.length) return [];
    return [...weatherData].reverse().map(record => {
      const w = record.weather_data || {};
      return {
        time: new Date(record.timestamp).getTime(),
        timeLabel: formatTimeLabel(record.timestamp, selectedRange.hours),
        temperature: w.temperature_c ?? w.temperature,
        dewPoint: w.dew_point_c ?? w.dewpoint ?? w.dewPoint,
        humidity: w.humidity_percent ?? w.humidity,
        windSpeed: w.wind_speed_kph ?? w.wind_speed ?? w.windSpeed,
        cloudCover: w.cloud_cover_percent ?? w.cloud_cover ?? w.cloudCover,
        pressure: w.pressure_mbar ?? w.pressure,
        isSafe: record.is_safe ? 1 : 0,
      };
    });
  }, [weatherData, selectedRange]);

  const hasTemperature = chartData.some(d => d.temperature != null);
  const hasHumidity = chartData.some(d => d.humidity != null);
  const hasWindSpeed = chartData.some(d => d.windSpeed != null);
  const hasCloudCover = chartData.some(d => d.cloudCover != null);
  const hasPressure = chartData.some(d => d.pressure != null);
  const hasSafety = chartData.some(d => d.isSafe != null);

  const tickInterval = chartData.length > 0 ? Math.floor(chartData.length / 8) || 1 : 1;

  if (loading && !weatherData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !weatherData?.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No weather history available for this observatory. Weather data is recorded when a weather device is connected.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={selectedRange.label}
          exclusive
          onChange={handleRangeChange}
          size="small"
        >
          {TIME_RANGES.map(range => (
            <ToggleButton key={range.label} value={range.label}>
              {range.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              size="small"
            />
          }
          label="Auto-refresh"
        />
        <IconButton onClick={fetchData} size="small" disabled={loading}>
          <Refresh />
        </IconButton>
        {loading && <CircularProgress size={20} />}
      </Box>

      {/* Summary stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">Safe %</Typography>
                <Typography
                  variant="h6"
                  sx={{ color: (stats.safe_percentage ?? 0) >= 80 ? 'success.main' : 'warning.main' }}
                >
                  {stats.safe_percentage != null ? `${stats.safe_percentage.toFixed(0)}%` : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">Temperature</Typography>
                <Typography variant="h6">
                  {stats.temperature?.avg != null ? `${stats.temperature.avg.toFixed(1)}\u00b0C` : 'N/A'}
                </Typography>
                {stats.temperature?.min != null && (
                  <Typography variant="caption" color="text.secondary">
                    {stats.temperature.min.toFixed(1)} / {stats.temperature.max.toFixed(1)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">Humidity</Typography>
                <Typography variant="h6">
                  {stats.humidity_avg != null ? `${stats.humidity_avg.toFixed(0)}%` : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">Wind Speed</Typography>
                <Typography variant="h6">
                  {stats.wind_speed?.avg != null ? `${stats.wind_speed.avg.toFixed(1)} kph` : 'N/A'}
                </Typography>
                {stats.wind_speed?.max != null && (
                  <Typography variant="caption" color="text.secondary">
                    max {stats.wind_speed.max.toFixed(1)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">Cloud Cover</Typography>
                <Typography variant="h6">
                  {stats.cloud_cover_avg != null ? `${stats.cloud_cover_avg.toFixed(0)}%` : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">Readings</Typography>
                <Typography variant="h6">
                  {stats.total_readings != null ? stats.total_readings.toLocaleString() : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      {chartData.length === 0 ? (
        <Alert severity="info">
          No weather data available for the selected time range.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {hasTemperature && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Temperature & Dew Point</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" interval={tickInterval} fontSize={11} />
                    <YAxis fontSize={11} />
                    <ChartTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="temperature" stroke="#ff7043" dot={false} name="Temperature (\u00b0C)" />
                    <Line type="monotone" dataKey="dewPoint" stroke="#42a5f5" dot={false} name="Dew Point (\u00b0C)" />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {hasHumidity && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Humidity</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" interval={tickInterval} fontSize={11} />
                    <YAxis fontSize={11} />
                    <ChartTooltip />
                    <ReferenceLine y={85} stroke="#f44336" strokeDasharray="3 3" label="85%" />
                    <Area type="monotone" dataKey="humidity" stroke="#66bb6a" fill="#66bb6a" fillOpacity={0.3} dot={false} name="Humidity (%)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {hasWindSpeed && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Wind Speed</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" interval={tickInterval} fontSize={11} />
                    <YAxis fontSize={11} />
                    <ChartTooltip />
                    <Line type="monotone" dataKey="windSpeed" stroke="#ab47bc" dot={false} name="Wind Speed (kph)" />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {hasCloudCover && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Cloud Cover</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" interval={tickInterval} fontSize={11} />
                    <YAxis domain={[0, 100]} fontSize={11} />
                    <ChartTooltip />
                    <Area type="monotone" dataKey="cloudCover" stroke="#78909c" fill="#78909c" fillOpacity={0.3} dot={false} name="Cloud Cover (%)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {hasPressure && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Pressure</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" interval={tickInterval} fontSize={11} />
                    <YAxis domain={['auto', 'auto']} fontSize={11} />
                    <ChartTooltip />
                    <Line type="monotone" dataKey="pressure" stroke="#26a69a" dot={false} name="Pressure (mbar)" />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {hasSafety && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }} elevation={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Safety</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" interval={tickInterval} fontSize={11} />
                    <YAxis domain={[0, 1]} ticks={[0, 1]} fontSize={11} />
                    <ChartTooltip />
                    <Area type="stepAfter" dataKey="isSafe" stroke="#4caf50" fill="#4caf50" fillOpacity={0.3} dot={false} name="Safe" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default WeatherHistorySection;
